import {
  CompactSelection,
  EditableGridCell,
  GridSelection,
  Item,
} from "@glideapps/glide-data-grid";
import { getErrorMessage } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import {
  AI_SCRIPT_CHAT_CONTEXT_KIND,
  AI_SPREADSHEET_VALUE_TYPE,
} from "@/payroll/ai-script/types";
import type {
  AiScriptChatSelectionContext,
  IAiSpreadsheetCell,
  IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import { isAiScriptWorkbookAuthorizationError } from "@/payroll/ai-script/utils";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { getSuggestedValueCellValue } from "./suggested-value-cell";

// The maximized viewer is a fixed overlay at z-[900], while portaled overlay
// content (popovers, tooltips) defaults to z-50 — anything opened from inside
// the viewer needs this class to stack above it in full screen.
export const VIEWER_OVERLAY_CLASSNAME = "z-[950]";
export const SHEET_PREFETCH_DWELL_MS = 250;

export const emptySelection: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
};

export const formatColumnLetter = (colIndex: number) => {
  let columnNumber = colIndex + 1;
  let columnTitle = "";

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    columnTitle = String.fromCharCode(65 + remainder) + columnTitle;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return columnTitle;
};

export const formatCoordinate = (cell: Item) =>
  `${formatColumnLetter(cell[0])}${cell[1] + 1}`;

export const getErrorTitle = (error: unknown) => {
  if (error instanceof ApiError && error.status === 404) {
    return getErrorMessage(error);
  }

  if (error instanceof ApiError && error.status === 401) {
    return "Session expired";
  }

  if (isAiScriptWorkbookAuthorizationError(error)) {
    return "Grid access changed";
  }

  return "Unable to load the Grid";
};

export const getCellErrorCode = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell?.error) return null;
  if (typeof cell.error === "string") return cell.error;

  return cell.error.code ?? cell.error.message ?? null;
};

export const getFormulaBarValue = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell) return "";
  if (cell.raw_input != null) return cell.raw_input;
  if (cell.computed_value != null) return String(cell.computed_value);

  return "";
};

export const getEditedRawInput = (newValue: EditableGridCell) => {
  const value = (newValue as { data?: unknown }).data;
  const suggestedValue = getSuggestedValueCellValue(value);

  return suggestedValue ?? String(value ?? "");
};

export interface GridViewportSnapshot {
  sheetId: string;
  scrollLeft: number;
  scrollTop: number;
}

const GRID_SCROLLER_CLASSNAME = "dvn-scroller";

export const isGridScroller = (
  target: EventTarget | null,
): target is HTMLElement =>
  target instanceof HTMLElement &&
  target.classList.contains(GRID_SCROLLER_CLASSNAME);

const getGridScroller = (gridWrapper: HTMLElement | null) =>
  gridWrapper?.querySelector<HTMLElement>(`.${GRID_SCROLLER_CLASSNAME}`) ??
  null;

export const captureGridViewport = (
  sheetId: string,
  scroller: Pick<HTMLElement, "scrollLeft" | "scrollTop">,
): GridViewportSnapshot => ({
  sheetId,
  scrollLeft: scroller.scrollLeft,
  scrollTop: scroller.scrollTop,
});

// The grid's data adapter is rebuilt when React Query applies optimistic edits
// and authoritative recalculation results. Glide can reset its native scroller
// while reconciling those new data/layout props, so put the same sheet back at
// its exact pixel offset. A sheet id guard keeps initial loads and intentional
// sheet navigation out of this path.
export const restoreGridViewport = ({
  gridWrapper,
  activeSheetId,
  snapshot,
}: {
  gridWrapper: HTMLElement | null;
  activeSheetId: string;
  snapshot: GridViewportSnapshot | null;
}) => {
  if (!snapshot || snapshot.sheetId !== activeSheetId) return false;

  const scroller = getGridScroller(gridWrapper);
  if (!scroller) return false;

  const hasMoved =
    Math.abs(scroller.scrollLeft - snapshot.scrollLeft) > 1 ||
    Math.abs(scroller.scrollTop - snapshot.scrollTop) > 1;
  if (!hasMoved) return false;

  scroller.scrollLeft = snapshot.scrollLeft;
  scroller.scrollTop = snapshot.scrollTop;
  scroller.dispatchEvent(new Event("scroll"));
  return true;
};

const GRID_VIEWPORT_RESTORE_DELAYS_MS = [40, 120];

export const scheduleGridViewportRestore = (
  params: Parameters<typeof restoreGridViewport>[0],
) => {
  const restore = () => restoreGridViewport(params);
  restore();
  const animationFrame = requestAnimationFrame(restore);
  const timers = GRID_VIEWPORT_RESTORE_DELAYS_MS.map((delay) =>
    window.setTimeout(restore, delay),
  );

  return () => {
    cancelAnimationFrame(animationFrame);
    timers.forEach(window.clearTimeout);
  };
};

// Glide renders only the first line in non-wrapped cells. Collapse embedded
// line breaks so imported text remains visible without changing wrapped cells.
export const normalizeSingleLineCellText = (
  value: string,
  cell: IAiSpreadsheetCell,
) =>
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  cell.format?.alignment?.wrap_text !== true
    ? value.replace(/\s*[\r\n]+\s*/g, " ")
    : value;

const GRID_SCROLL_REASSERT_DELAYS_MS = [40, 120, 220];

export const getGridRowScrollTarget = ({
  row,
  viewportHeight,
  scrollHeight,
  getRowHeight,
}: {
  row: number;
  viewportHeight: number;
  scrollHeight: number;
  getRowHeight: (row: number) => number;
}) => {
  let rowTop = 0;
  for (let index = 0; index < row; index += 1) {
    rowTop += getRowHeight(index);
  }

  const centeredTarget = rowTop + getRowHeight(row) / 2 - viewportHeight / 2;
  const maxScrollTop = Math.max(0, scrollHeight - viewportHeight);

  return Math.min(Math.max(0, centeredTarget), maxScrollTop);
};

// Glide can badly overshoot distant, unrendered rows because its vertical
// estimate differs from the rendered row height. Drive its native scroller to
// the real pixel target and briefly re-assert that position while Glide finishes
// its own selection-pinning renders. Horizontal position is left unchanged so
// row identity columns remain visible.
export const scheduleGridRowScroll = ({
  gridWrapper,
  row,
  getRowHeight,
}: {
  gridWrapper: HTMLElement | null;
  row: number;
  getRowHeight: (row: number) => number;
}) => {
  const scrollToRow = () => {
    const scroller = getGridScroller(gridWrapper);
    if (!scroller) return;

    const target = getGridRowScrollTarget({
      row,
      viewportHeight: scroller.clientHeight,
      scrollHeight: scroller.scrollHeight,
      getRowHeight,
    });
    if (Math.abs(scroller.scrollTop - target) <= 2) return;

    scroller.scrollTop = target;
    scroller.dispatchEvent(new Event("scroll"));
  };

  const animationFrame = requestAnimationFrame(scrollToRow);
  const timers = GRID_SCROLL_REASSERT_DELAYS_MS.map((delay) =>
    window.setTimeout(scrollToRow, delay),
  );

  return () => {
    cancelAnimationFrame(animationFrame);
    timers.forEach(window.clearTimeout);
  };
};

const MAX_CONTEXT_ENTRIES = 40;

const getCellDisplayValue = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell) return null;
  if (cell.computed_value != null) return String(cell.computed_value);

  return cell.raw_input ?? null;
};

export const buildAiScriptChatSelectionContext = ({
  adaptedSheet,
  activeSheet,
  gridSelection,
  getSavedRow,
}: {
  adaptedSheet: AdaptedSpreadsheetSheet;
  activeSheet: IAiSpreadsheetLoadedSheet;
  gridSelection: GridSelection;
  getSavedRow: (displayRow: number) => number;
}): AiScriptChatSelectionContext | null => {
  const selectedRow = gridSelection.rows.first();
  const selectedColumn = gridSelection.columns.first();
  const selectedCell = gridSelection.current?.cell ?? null;

  if (selectedRow !== undefined) {
    const cells = adaptedSheet.columns
      .map((_, colIndex) => ({
        header: getCellDisplayValue(adaptedSheet.getCellRecord(colIndex, 0)),
        value: getCellDisplayValue(
          adaptedSheet.getCellRecord(colIndex, selectedRow),
        ),
      }))
      .filter(
        (entry): entry is { header: string | null; value: string } =>
          entry.value != null,
      )
      .slice(0, MAX_CONTEXT_ENTRIES);

    return {
      kind: AI_SCRIPT_CHAT_CONTEXT_KIND.row,
      sheetName: activeSheet.name,
      rowNumber: getSavedRow(selectedRow) + 1,
      rowLabel: getCellDisplayValue(adaptedSheet.getCellRecord(0, selectedRow)),
      cells,
    };
  }

  if (selectedColumn !== undefined) {
    const allValues: { rowLabel: string | null; value: string }[] = [];
    for (let row = 1; row < adaptedSheet.rowCount; row += 1) {
      const value = getCellDisplayValue(
        adaptedSheet.getCellRecord(selectedColumn, row),
      );
      if (value == null) continue;
      allValues.push({
        rowLabel: getCellDisplayValue(adaptedSheet.getCellRecord(0, row)),
        value,
      });
    }

    return {
      kind: AI_SCRIPT_CHAT_CONTEXT_KIND.column,
      sheetName: activeSheet.name,
      columnLetter: formatColumnLetter(selectedColumn),
      columnHeader: getCellDisplayValue(
        adaptedSheet.getCellRecord(selectedColumn, 0),
      ),
      values: allValues.slice(0, MAX_CONTEXT_ENTRIES),
      isTruncated: allValues.length > MAX_CONTEXT_ENTRIES,
    };
  }

  if (!selectedCell) return null;

  const savedRow = getSavedRow(selectedCell[1]) + 1;
  const savedCol = selectedCell[0] + 1;
  const region = activeSheet.regions?.find(
    (candidate) =>
      candidate.validation.valid &&
      savedRow >= candidate.header_row &&
      savedRow <= candidate.record_end_row &&
      savedCol >= candidate.start_col &&
      savedCol <= candidate.end_col,
  );
  // Region coordinates belong to the saved sheet, not the sorted/filtered view.
  const headerCell = region
    ? activeSheet.cells.find(
        (cell) => cell.row === region.header_row && cell.col === savedCol,
      )
    : adaptedSheet.getCellRecord(selectedCell[0], 0);

  return {
    kind: AI_SCRIPT_CHAT_CONTEXT_KIND.cell,
    sheetName: activeSheet.name,
    coordinate: formatCoordinate([
      selectedCell[0],
      getSavedRow(selectedCell[1]),
    ]),
    value: getCellDisplayValue(
      adaptedSheet.getCellRecord(selectedCell[0], selectedCell[1]),
    ),
    columnHeader: getCellDisplayValue(headerCell),
    rowLabel: getCellDisplayValue(
      adaptedSheet.getCellRecord(0, selectedCell[1]),
    ),
  };
};

// A menu question always targets one populated cell, regardless of the current
// row, column, or range selection. Keep the saved-row mapping used by chat.
export const buildAiScriptChatCellQuestion = ({
  adaptedSheet,
  activeSheet,
  cell,
  getSavedRow,
}: {
  adaptedSheet: AdaptedSpreadsheetSheet;
  activeSheet: IAiSpreadsheetLoadedSheet;
  cell: Item;
  getSavedRow: (displayRow: number) => number;
}) => {
  const [col, row] = cell;
  const selection: GridSelection = {
    ...emptySelection,
    current: {
      cell,
      range: { x: col, y: row, width: 1, height: 1 },
      rangeStack: [],
    },
  };
  const context = buildAiScriptChatSelectionContext({
    adaptedSheet,
    activeSheet,
    gridSelection: selection,
    getSavedRow,
  });
  return context?.kind === AI_SCRIPT_CHAT_CONTEXT_KIND.cell &&
    context.value?.trim()
    ? { selection, context }
    : null;
};
